import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  Field,
  FormActionBar,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  HeaderCell,
  HeaderControlCell,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  checkboxClass,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getNoaOrderForm,
  updateNoaOrderForm,
} from "../../../../../services/patientService";
import {
  CalendarIcon,
  DeleteIcon,
  DoctorIcon,
  MedicalNotesIcon,
  PlusIcon,
  ShieldCheckIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { PATIENT_THEME } from "../../../patientTheme";
import {
  buildInsurances,
  buildPhysicians,
} from "../../../patientForm.shared";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import { resolveFormActorRole } from "../disciplineFormAccess";
import {
  CERTIFICATION_ITEMS,
  DEFAULT_FUNCTIONAL_LIMITATIONS,
  defaultVisitFrequencies,
  newVisitRowId,
  sumVisitFrequencies,
} from "./noaOrderContent";

const ORDER_DATE_MAX = new Date().toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

const emptyValues = () => ({
  orderDate: today(),
  insuranceProvider: "",
  facilityName: "",
  socDate: "",
  physicianName: "",
  physicianNpi: "",
  episodePeriod: "",
  primaryDiagnosisNarrative: "",
  nutritionalRequirements: "",
  functionalLimitations: [],
  visitFrequencies: defaultVisitFrequencies(),
  certHomeboundSkilled: false,
  certUnderPhysicianCare: false,
  certFaceToFace: false,
  faceToFaceDate: "",
  faceToFacePhysician: "",
  verbalOrderReceivedBy: "",
  verbalOrderDate: "",
  clinicianSignatureName: "",
  reviewComments: "",
});

const caregiverLabel = (form) => {
  const name = form?.filledByName || "";
  const role = form?.filledByRole || "";
  if (name && role) return `${name} (${role})`;
  return name || role || "—";
};

const formatEpisodePeriod = (episode) => {
  if (!episode) return "";
  const from = displayDateUS(episode.startDate || episode.socDate || episode.fromDate);
  const to = displayDateUS(episode.endDate || episode.toDate);
  if (from && to) return `${from} - ${to}`;
  return from || to || "";
};

const NoaOrderForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [customLimitation, setCustomLimitation] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const freqs = Array.isArray(data.visitFrequencies)
        ? data.visitFrequencies
        : [];
      setValues({
        orderDate: data.orderDate || today(),
        insuranceProvider: data.insuranceProvider || "",
        facilityName: data.facilityName || "",
        socDate: data.socDate || "",
        physicianName: data.physicianName || "",
        physicianNpi: data.physicianNpi || "",
        episodePeriod: data.episodePeriod || "",
        primaryDiagnosisNarrative: data.primaryDiagnosisNarrative || "",
        nutritionalRequirements: data.nutritionalRequirements || "",
        functionalLimitations: Array.isArray(data.functionalLimitations)
          ? data.functionalLimitations
          : [],
        visitFrequencies:
          freqs.length > 0
            ? freqs.map((row) => ({
                id: row.id || newVisitRowId(),
                discipline: row.discipline || "",
                freq1: row.freq1 || "",
                freq2: row.freq2 || "",
                freq3: row.freq3 || "",
                totalVisits:
                  row.totalVisits === 0 || row.totalVisits
                    ? Number(row.totalVisits)
                    : null,
              }))
            : defaultVisitFrequencies(),
        certHomeboundSkilled: Boolean(data.certHomeboundSkilled),
        certUnderPhysicianCare: Boolean(data.certUnderPhysicianCare),
        certFaceToFace: Boolean(data.certFaceToFace),
        faceToFaceDate: data.faceToFaceDate || "",
        faceToFacePhysician: data.faceToFacePhysician || "",
        verbalOrderReceivedBy: data.verbalOrderReceivedBy || "",
        verbalOrderDate: data.verbalOrderDate || "",
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      });
      resetSignatures();
    },
    [resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const insurers = buildInsurances(patient);
    const physicians = buildPhysicians(patient);
    const primaryIns = insurers[0];
    const primaryPhys = physicians[0];
    const episodes = Array.isArray(patient?.episodes) ? patient.episodes : [];
    const latestEpisode = episodes[0];

    if (isDraft) {
      hydrate({
        formName:
          clinicalForm.formName || draft?.formName || "Notice of Admission",
        formType: clinicalForm.formType || draft?.formType,
        formSection: clinicalForm.formSection || draft?.formSection,
        status: "pending",
        patientName,
        ...draftCaregiverIdentity(currentUser, role),
        permissions: buildClinicalFormPermissions("pending", role),
        insuranceProvider: primaryIns?.insuranceName || "",
        physicianName: primaryPhys?.physicianName || "",
        physicianNpi: primaryPhys?.npiNumber || "",
        socDate: latestEpisode?.socDate || latestEpisode?.startDate || "",
        episodePeriod: formatEpisodePeriod(latestEpisode),
        faceToFacePhysician: primaryPhys?.physicianName || "",
      });
      if (!cancelled) {
        setLoading(false);
        onReady?.();
      }
      return () => {
        cancelled = true;
      };
    }

    getNoaOrderForm(patientId, formId)
      .then((res) => {
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        hydrate({
          ...loaded,
          insuranceProvider:
            loaded.insuranceProvider || primaryIns?.insuranceName || "",
          physicianName:
            loaded.physicianName || primaryPhys?.physicianName || "",
          physicianNpi: loaded.physicianNpi || primaryPhys?.npiNumber || "",
          socDate:
            loaded.socDate ||
            latestEpisode?.socDate ||
            latestEpisode?.startDate ||
            "",
          episodePeriod:
            loaded.episodePeriod || formatEpisodePeriod(latestEpisode),
          faceToFacePhysician:
            loaded.faceToFacePhysician ||
            primaryPhys?.physicianName ||
            "",
        });
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        onReady?.();
      });
    return () => {
      cancelled = true;
    };
    // Load once per form identity — unstable callbacks must not retrigger fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleLimitation = (tag) => {
    setValues((prev) => {
      const has = prev.functionalLimitations.includes(tag);
      return {
        ...prev,
        functionalLimitations: has
          ? prev.functionalLimitations.filter((t) => t !== tag)
          : [...prev.functionalLimitations, tag],
      };
    });
  };

  const addCustomLimitation = () => {
    const tag = customLimitation.trim();
    if (!tag) return;
    setValues((prev) => {
      if (prev.functionalLimitations.includes(tag)) return prev;
      return {
        ...prev,
        functionalLimitations: [...prev.functionalLimitations, tag],
      };
    });
    setCustomLimitation("");
  };

  const updateVisitRow = (rowId, patch) => {
    setValues((prev) => ({
      ...prev,
      visitFrequencies: prev.visitFrequencies.map((row) => {
        if (row.id !== rowId) return row;
        const next = { ...row, ...patch };
        if (
          patch.freq1 !== undefined ||
          patch.freq2 !== undefined ||
          patch.freq3 !== undefined
        ) {
          const computed = sumVisitFrequencies(next);
          if (computed != null) next.totalVisits = computed;
        }
        return next;
      }),
    }));
  };

  const removeVisitRow = (rowId) => {
    setValues((prev) => ({
      ...prev,
      visitFrequencies: prev.visitFrequencies.filter((r) => r.id !== rowId),
    }));
  };

  const addVisitRow = () => {
    setValues((prev) => ({
      ...prev,
      visitFrequencies: [
        ...prev.visitFrequencies,
        {
          id: newVisitRowId(),
          discipline: "",
          freq1: "",
          freq2: "",
          freq3: "",
          totalVisits: null,
        },
      ],
    }));
  };

  const { permissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId: form?.filledBy || clinicalForm?.filledBy || (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });
  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const payload = {
      ...values,
      flag,
      orderDate: values.orderDate
        ? formatDateForInput(values.orderDate)
        : null,
      socDate: values.socDate ? formatDateForInput(values.socDate) : null,
      faceToFaceDate: values.faceToFaceDate
        ? formatDateForInput(values.faceToFaceDate)
        : null,
      verbalOrderDate: values.verbalOrderDate
        ? formatDateForInput(values.verbalOrderDate)
        : null,
      functionalLimitations: values.functionalLimitations || [],
      visitFrequencies: (values.visitFrequencies || [])
        .filter((row) => row.discipline?.trim())
        .map((row) => ({
          id: row.id,
          discipline: row.discipline.trim(),
          freq1: row.freq1 || "",
          freq2: row.freq2 || "",
          freq3: row.freq3 || "",
          totalVisits:
            row.totalVisits === "" || row.totalVisits == null
              ? null
              : Number(row.totalVisits),
        })),
    };

    // Never send patient signature fields on NOA.
    delete payload.patientSignatureName;

    if (!canSign) return payload;
    const sig = signatures.buildSignaturePayload();
    delete sig.patientSignatureImage;
    delete sig.clearPatientSignature;
    return { ...payload, ...sig };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateNoaOrderForm,
        filledByUserId,
        scheduleDate: values.orderDate
          ? formatDateForInput(values.orderDate)
          : null,
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const limitationOptions = Array.from(
    new Set([
      ...DEFAULT_FUNCTIONAL_LIMITATIONS,
      ...values.functionalLimitations,
    ]),
  );

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Notice of Admission (NOA)
          </h1>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <section className="rounded-md border border-slate-800 overflow-hidden">
          <div className="grid grid-cols-5 gap-x-2.5 items-start px-3 py-1.5">
            <HeaderCell
              label="Patient Name"
              value={patientName || form.patientName}
              dense
            />
            <HeaderCell label="MR Number" value={patient?.mrn} dense />
            <HeaderCell
              label="Date of Birth"
              value={displayDateUS(
                patient?.dateOfBirth || form.patientDateOfBirth,
              )}
              dense
            />
            {caregiverSlot || (
              <HeaderCell
                label="Caregiver Name"
                value={caregiverLabel(form)}
                dense
              />
            )}
            {printMode ? (
              <HeaderCell
                label="Date"
                value={displayDateUS(values.orderDate)}
                dense
              />
            ) : (
              <HeaderControlCell label="Date">
                <InputDatePicker
                  name="orderDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.orderDate) || ""}
                  onChange={(event) =>
                    setValue("orderDate", event.target.value)
                  }
                  max={ORDER_DATE_MAX}
                  className="min-w-0 w-full"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </HeaderControlCell>
            )}
          </div>
        </section>

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <Section icon={CalendarIcon} title="Admission Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-2">
            <Field label="Insurance Provider">
              <input
                type="text"
                className={fieldClass}
                value={values.insuranceProvider}
                disabled={!canEdit}
                onChange={(e) => setValue("insuranceProvider", e.target.value)}
              />
            </Field>
            <Field label="Facility Name">
              <input
                type="text"
                className={fieldClass}
                value={values.facilityName}
                disabled={!canEdit}
                onChange={(e) => setValue("facilityName", e.target.value)}
              />
            </Field>
            <Field label="Start of Care (SOC) Date">
              {printMode ? (
                <div className={`${fieldClass} flex items-center`}>
                  {displayDateUS(values.socDate) || "—"}
                </div>
              ) : (
                <InputDatePicker
                  name="socDate"
                  formatSeparator="/"
                  allowManualEntry
                  disabled={!canEdit}
                  value={formatDateForInput(values.socDate) || ""}
                  onChange={(event) => setValue("socDate", event.target.value)}
                  className="w-full"
                  inputClassName={`${fieldClass} bg-white`}
                />
              )}
            </Field>
            <Field label="Physician Name">
              <input
                type="text"
                className={fieldClass}
                value={values.physicianName}
                disabled={!canEdit}
                onChange={(e) => setValue("physicianName", e.target.value)}
              />
            </Field>
            <Field label="NPI Number">
              <input
                type="text"
                className={fieldClass}
                value={values.physicianNpi}
                disabled={!canEdit}
                onChange={(e) => setValue("physicianNpi", e.target.value)}
              />
            </Field>
            <Field label="Episode Period">
              <input
                type="text"
                className={fieldClass}
                value={values.episodePeriod}
                disabled={!canEdit}
                placeholder="MM/DD/YYYY - MM/DD/YYYY"
                onChange={(e) => setValue("episodePeriod", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={MedicalNotesIcon} title="Clinical Orders & Interventions">
          <div className="space-y-3">
            <Field label="Primary Diagnosis & Narrative">
              <textarea
                rows={4}
                className={textareaClass}
                value={values.primaryDiagnosisNarrative}
                disabled={!canEdit}
                placeholder="Primary diagnosis with ICD code and clinical narrative…"
                onChange={(e) =>
                  setValue("primaryDiagnosisNarrative", e.target.value)
                }
              />
            </Field>
            <Field label="Nutritional Requirements">
              <input
                type="text"
                className={fieldClass}
                value={values.nutritionalRequirements}
                disabled={!canEdit}
                placeholder="e.g. Regular as tolerated, low sodium"
                onChange={(e) =>
                  setValue("nutritionalRequirements", e.target.value)
                }
              />
            </Field>
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Functional Limitations
              </div>
              <div className="flex flex-wrap gap-1.5">
                {limitationOptions.map((tag) => {
                  const active = values.functionalLimitations.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      disabled={!canEdit}
                      onClick={() => toggleLimitation(tag)}
                      className={`h-7 px-2.5 rounded text-[11px] font-medium border transition-colors disabled:opacity-60 ${
                        active
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-indigo-50/60 text-indigo-800 border-indigo-100 hover:bg-indigo-100"
                      }`}
                    >
                      {tag}
                    </button>
                  );
                })}
                {canEdit && (
                  <div className="inline-flex items-center gap-1">
                    <input
                      type="text"
                      value={customLimitation}
                      onChange={(e) => setCustomLimitation(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addCustomLimitation();
                        }
                      }}
                      placeholder="Custom…"
                      className="h-7 w-28 px-2 text-[11px] border border-dashed border-indigo-300 rounded outline-none focus:border-indigo-400"
                    />
                    <button
                      type="button"
                      onClick={addCustomLimitation}
                      className={`inline-flex items-center gap-1 h-7 px-2 text-[11px] font-semibold rounded border ${PATIENT_THEME.button.outline}`}
                    >
                      <PlusIcon size={12} />
                      Add
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Section>

        <Section icon={CalendarIcon} title="Visit Frequency Matrix">
          <div className="overflow-x-auto rounded border border-slate-200">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr
                  className={`text-[10px] uppercase tracking-wide ${PATIENT_THEME.tableHead.className}`}
                >
                  <th className="px-2 py-1.5 font-semibold">Discipline</th>
                  <th className="px-2 py-1.5 font-semibold">Frequency 1</th>
                  <th className="px-2 py-1.5 font-semibold">Frequency 2</th>
                  <th className="px-2 py-1.5 font-semibold">Frequency 3</th>
                  <th className="px-2 py-1.5 font-semibold w-24">Total Visits</th>
                  {canEdit && (
                    <th className="px-2 py-1.5 font-semibold w-12 text-right">
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {values.visitFrequencies.map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        className={fieldClass}
                        value={row.discipline}
                        disabled={!canEdit}
                        placeholder="SN / PT / OT…"
                        onChange={(e) =>
                          updateVisitRow(row.id, {
                            discipline: e.target.value,
                          })
                        }
                      />
                    </td>
                    {["freq1", "freq2", "freq3"].map((key) => (
                      <td key={key} className="px-2 py-1">
                        <input
                          type="text"
                          className={fieldClass}
                          value={row[key] || ""}
                          disabled={!canEdit}
                          placeholder="2w1"
                          onChange={(e) =>
                            updateVisitRow(row.id, { [key]: e.target.value })
                          }
                        />
                      </td>
                    ))}
                    <td className="px-2 py-1">
                      <input
                        type="number"
                        min={0}
                        className={`${fieldClass} tabular-nums`}
                        value={row.totalVisits ?? ""}
                        disabled={!canEdit}
                        onChange={(e) =>
                          updateVisitRow(row.id, {
                            totalVisits:
                              e.target.value === ""
                                ? null
                                : Number(e.target.value),
                          })
                        }
                      />
                    </td>
                    {canEdit && (
                      <td className="px-2 py-1 text-right">
                        <button
                          type="button"
                          onClick={() => removeVisitRow(row.id)}
                          className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                          aria-label="Remove discipline"
                        >
                          <DeleteIcon size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {canEdit && (
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={addVisitRow}
                className={`inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border ${PATIENT_THEME.button.outline}`}
              >
                <PlusIcon size={13} />
                Add Discipline
              </button>
            </div>
          )}
        </Section>

        <Section icon={ShieldCheckIcon} title="Certifications & Acknowledgments">
          <div className={`rounded-md border p-3 space-y-2.5 ${PATIENT_THEME.accentSoft}`}>
            {CERTIFICATION_ITEMS.map((item) => (
              <label
                key={item.key}
                className="flex items-start gap-2 text-[12px] text-slate-700 cursor-pointer"
              >
                <input
                  type="checkbox"
                  className={checkboxClass}
                  checked={Boolean(values[item.key])}
                  disabled={!canEdit}
                  onChange={(e) => setValue(item.key, e.target.checked)}
                />
                <span>{item.label}</span>
              </label>
            ))}

            <div className="pt-1 border-t border-indigo-100/80">
              <label className="flex items-start gap-2 text-[12px] text-slate-700">
                <input
                  type="checkbox"
                  className={checkboxClass}
                  checked={values.certFaceToFace}
                  disabled={!canEdit}
                  onChange={(e) => setValue("certFaceToFace", e.target.checked)}
                />
                <span className="flex-1 min-w-0">
                  Face-to-Face encounter was completed on{" "}
                  {printMode ? (
                    <strong>{displayDateUS(values.faceToFaceDate) || "—"}</strong>
                  ) : (
                    <span className="inline-block align-middle w-[9.5rem] mx-1">
                      <InputDatePicker
                        name="faceToFaceDate"
                        formatSeparator="/"
                        allowManualEntry
                        compact
                        disabled={!canEdit}
                        value={formatDateForInput(values.faceToFaceDate) || ""}
                        onChange={(event) =>
                          setValue("faceToFaceDate", event.target.value)
                        }
                        className="w-full"
                        inputClassName="w-full h-6 px-1.5 text-[11px] font-semibold bg-white border border-indigo-200 rounded outline-none"
                      />
                    </span>
                  )}{" "}
                  by physician{" "}
                  {printMode ? (
                    <strong>{values.faceToFacePhysician || "—"}</strong>
                  ) : (
                    <input
                      type="text"
                      className="inline-block align-middle w-44 h-6 mx-1 px-1.5 text-[11px] font-semibold bg-white border border-indigo-200 rounded outline-none"
                      value={values.faceToFacePhysician}
                      disabled={!canEdit}
                      placeholder="Physician name"
                      onChange={(e) =>
                        setValue("faceToFacePhysician", e.target.value)
                      }
                    />
                  )}
                  .
                </span>
              </label>
            </div>
          </div>
        </Section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div>
            <FormSignatureSections
              {...signatureProps}
              showPatientSection={false}
              clinicianNameValue={values.clinicianSignatureName}
              onClinicianSign={handleClinicianSign}
              canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
              printMode={printMode}
              clinicianSectionTitle="Clinician Authentication"
            />
          </div>

          <Section icon={DoctorIcon} title="Physician Verification">
            <div className="space-y-2">
              <Field label="Verbal Order Received By">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.verbalOrderReceivedBy}
                  disabled={!canEdit}
                  placeholder="Clinician name, credentials"
                  onChange={(e) =>
                    setValue("verbalOrderReceivedBy", e.target.value)
                  }
                />
              </Field>
              <Field label="Date of Verbal Order">
                {printMode ? (
                  <div className={`${fieldClass} flex items-center`}>
                    {displayDateUS(values.verbalOrderDate) || "—"}
                  </div>
                ) : (
                  <InputDatePicker
                    name="verbalOrderDate"
                    formatSeparator="/"
                    allowManualEntry
                    disabled={!canEdit}
                    value={formatDateForInput(values.verbalOrderDate) || ""}
                    onChange={(event) =>
                      setValue("verbalOrderDate", event.target.value)
                    }
                    className="w-full"
                    inputClassName={`${fieldClass} bg-white`}
                  />
                )}
              </Field>
              <div
                className={`mt-2 rounded-md border px-3 py-2 text-[11px] leading-relaxed ${PATIENT_THEME.accentSoft}`}
              >
                Pending physician&apos;s electronic signature via DocuLink
                Portal. Automated notification sent to the attending
                physician&apos;s office.
              </div>
            </div>
          </Section>
        </div>

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      <FormActionBar
        permissions={permissions}
        status={form.status}
        canEdit={canEdit}
        saving={saving}
        onSubmit={submit}
        printMode={printMode}
      />
    </div>
  );
};

NoaOrderForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  /** Present when opened from the picker before any server row exists. */
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  /** Called with the new clinical-form id after the first draft save. */
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default NoaOrderForm;
